import { useQuery } from '@tanstack/react-query';

import { getAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { PendingCountDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { DEVICE_RENTALS_FEATURE_KEY, MANAGEMENT_DEVICE_REQUESTS_PENDING_COUNT_URL, deviceBadgeTotal } from '@/helpers/paymentDevices';

const POLL_INTERVAL = 60000;

/**
 * The office's counts for «מכשירי סליקה» (HAT-1434): requests waiting for approval, and devices in a
 * continuous rental of 3 months or more. Off with the device-rentals flag, so nothing polls for a
 * screen nobody can open; every reader shares one cache entry.
 */
const usePaymentDeviceCounts = (): PendingCountDtoIn | undefined => {
  const { isCurrentUserAdmin, isCurrentUserDebugger, currentUser } = useAuthContext();
  const isFeatureOn = useFeature(DEVICE_RENTALS_FEATURE_KEY);
  const canAccess = isFeatureOn && (isCurrentUserAdmin() || isCurrentUserDebugger()) && !!currentUser?.token;
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data } = useQuery({
    queryKey: [MANAGEMENT_DEVICE_REQUESTS_PENDING_COUNT_URL],
    queryFn: ({ signal }) => getAuth<PendingCountDtoIn>(MANAGEMENT_DEVICE_REQUESTS_PENDING_COUNT_URL, headers, undefined, signal),
    enabled: canAccess,
    refetchInterval: POLL_INTERVAL,
  });

  return canAccess ? data : undefined;
};

/** How many terminal rental requests wait for the office - the requests tab's label. */
export default function usePaymentDeviceRequestsPendingCount(): number | undefined {
  return usePaymentDeviceCounts()?.count;
}

/** The sidebar badge: the requests waiting plus the devices out 3 months or more. */
export function usePaymentDeviceBadgeTotal(): number | undefined {
  const counts = usePaymentDeviceCounts();
  return counts ? deviceBadgeTotal(counts) : undefined;
}
